import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth, useTheme, API } from '../App';
import { toast } from 'sonner';
import { 
  CircleDot, Plus, Sun, Moon, LogOut, Coins, Settings,
  Image, Trash2, Eye, LayoutDashboard, Wand2, Bookmark
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Dashboard = () => {
  const { user, token, logout, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [artworks, setArtworks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchArtworks();
  }, []);

  const fetchArtworks = async () => {
    try {
      const res = await fetch(`${API}/artworks`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setArtworks(data);
      }
    } catch (err) {
      console.error('Failed to fetch artworks:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const res = await fetch(`${API}/artworks/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      
      if (res.ok) {
        setArtworks(prev => prev.filter(a => a.artwork_id !== deleteId));
        toast.success('Artwork deleted');
      }
    } catch (err) {
      toast.error('Failed to delete artwork');
    } finally {
      setDeleteId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <CircleDot className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-medium">MandalaMuse</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 text-foreground"
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="text-sm">Gallery</span>
            </Link>
            <Link 
              to="/studio" 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-studio"
            >
              <Wand2 className="w-4 h-4" />
              <span className="text-sm">Studio</span>
            </Link>
            <Link 
              to="/presets" 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-presets"
            >
              <Bookmark className="w-4 h-4" />
              <span className="text-sm">Presets</span>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/credits" data-testid="credits-display">
              <Button variant="outline" size="sm" className="rounded-full gap-2">
                <Coins className="w-4 h-4 text-primary" />
                <span className="font-mono">{user?.credits || 0}</span>
              </Button>
            </Link>

            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              data-testid="dashboard-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/credits" className="cursor-pointer" data-testid="menu-credits">
                    <Coins className="w-4 h-4 mr-2" />
                    Buy Credits
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/presets" className="cursor-pointer" data-testid="menu-presets">
                    <Bookmark className="w-4 h-4 mr-2" />
                    My Presets
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div>
              <h1 className="font-heading text-4xl mb-2" data-testid="dashboard-title">Your Sacred Gallery</h1>
              <p className="text-muted-foreground">
                {artworks.length} artwork{artworks.length !== 1 ? 's' : ''} created
              </p>
            </div>
            <Link to="/studio">
              <Button className="rounded-full gap-2" data-testid="create-new-btn">
                <Plus className="w-5 h-5" />
                Create New
              </Button>
            </Link>
          </div>

          {/* Gallery */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : artworks.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-12 text-center"
              data-testid="empty-gallery"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Image className="w-10 h-10 text-primary" />
              </div>
              <h2 className="font-heading text-2xl mb-2">Your gallery awaits</h2>
              <p className="text-muted-foreground mb-6">
                Create your first sacred geometry artwork in the studio.
              </p>
              <Link to="/studio">
                <Button className="rounded-full" data-testid="empty-create-btn">
                  Enter the Studio
                </Button>
              </Link>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {artworks.map((artwork, index) => (
                <motion.div
                  key={artwork.artwork_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-card group overflow-hidden"
                  data-testid={`artwork-card-${artwork.artwork_id}`}
                >
                  <div className="aspect-square relative overflow-hidden">
                    <img
                      src={`data:image/png;base64,${artwork.main_image_base64}`}
                      alt={artwork.intention}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4 gap-2">
                      <Link to={`/artwork/${artwork.artwork_id}`}>
                        <Button size="sm" className="rounded-full gap-1" data-testid={`view-artwork-${artwork.artwork_id}`}>
                          <Eye className="w-4 h-4" />
                          View
                        </Button>
                      </Link>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        className="rounded-full gap-1"
                        onClick={() => setDeleteId(artwork.artwork_id)}
                        data-testid={`delete-artwork-${artwork.artwork_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg truncate capitalize">{artwork.intention}</h3>
                    <p className="text-sm text-muted-foreground capitalize">
                      {artwork.tradition.replace('_', ' ')} • {artwork.geometry_framework.replace(/_/g, ' ')}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Artwork</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this artwork? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
